import React, {
  useState,
} from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import {
  useSelector,
} from "react-redux";

import {
  auth,
} from "../firebase/config";

const AccountPage = () => {
  const toast = useToast();

  const { user } = useSelector(
    (state) => state.auth
  );

  const [currentPassword, setCurrentPassword] =
    useState("");
  const [newPassword, setNewPassword] =
    useState("");
  const [
    confirmNewPassword,
    setConfirmNewPassword,
  ] = useState("");
  const [loading, setLoading] =
    useState(false);
  const [errorMessage, setErrorMessage] =
    useState("");

  const handlePasswordChange = async (
    event
  ) => {
    event.preventDefault();
    setErrorMessage("");

    if (!currentPassword) {
      setErrorMessage(
        "Enter your current password."
      );
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage(
        "Your new password must contain at least 8 characters."
      );
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setErrorMessage(
        "The new passwords do not match."
      );
      return;
    }

    if (currentPassword === newPassword) {
      setErrorMessage(
        "Your new password must be different from your current password."
      );
      return;
    }

    const currentUser = auth.currentUser;

    if (
      !currentUser ||
      !currentUser.email
    ) {
      setErrorMessage(
        "Your account session is unavailable. Sign out and sign in again."
      );
      return;
    }

    setLoading(true);

    try {
      const credential =
        EmailAuthProvider.credential(
          currentUser.email,
          currentPassword
        );

      /*
       * Firebase requires recent authentication before
       * allowing a sensitive password change.
       */
      await reauthenticateWithCredential(
        currentUser,
        credential
      );

      await updatePassword(
        currentUser,
        newPassword
      );

      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");

      toast({
        title: "Password updated",
        description:
          "Your password was changed successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Password change failed:",
        {
          code: error.code || "unknown",
        }
      );

      let message =
        "Your password could not be changed. Please try again.";

      if (
        error.code ===
          "auth/invalid-credential" ||
        error.code ===
          "auth/wrong-password"
      ) {
        message =
          "Your current password is incorrect.";
      } else if (
        error.code ===
        "auth/weak-password"
      ) {
        message =
          "The new password is not strong enough.";
      } else if (
        error.code ===
        "auth/too-many-requests"
      ) {
        message =
          "Too many attempts were made. Please wait before trying again.";
      } else if (
        error.code ===
        "auth/requires-recent-login"
      ) {
        message =
          "For security, sign out, sign back in, and try again.";
      }

      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      maxW="700px"
      mx="auto"
      p={{ base: 4, md: 6 }}
    >
      <Box
        bg="white"
        borderWidth="1px"
        borderRadius="lg"
        shadow="sm"
        p={{ base: 5, md: 8 }}
      >
        <Heading
          size="lg"
          color="teal.600"
        >
          My Account
        </Heading>

        <Text
          mt={2}
          color="gray.600"
        >
          Signed in as{" "}
          <strong>
            {user?.email || "Unknown email"}
          </strong>
        </Text>

        <Heading
          size="md"
          mt={8}
          mb={4}
        >
          Change Password
        </Heading>

        <form onSubmit={handlePasswordChange}>
          <Stack spacing={4}>
            {errorMessage && (
              <Alert status="error">
                <AlertIcon />
                {errorMessage}
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel>
                Current Password
              </FormLabel>

              <Input
                type="password"
                value={currentPassword}
                onChange={(event) =>
                  setCurrentPassword(
                    event.target.value
                  )
                }
                autoComplete="current-password"
                disabled={loading}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                New Password
              </FormLabel>

              <Input
                type="password"
                value={newPassword}
                onChange={(event) =>
                  setNewPassword(
                    event.target.value
                  )
                }
                autoComplete="new-password"
                minLength={8}
                maxLength={128}
                disabled={loading}
              />

              <Text
                mt={1}
                fontSize="sm"
                color="gray.600"
              >
                Use at least 8 characters.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                Confirm New Password
              </FormLabel>

              <Input
                type="password"
                value={confirmNewPassword}
                onChange={(event) =>
                  setConfirmNewPassword(
                    event.target.value
                  )
                }
                autoComplete="new-password"
                minLength={8}
                maxLength={128}
                disabled={loading}
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText="Updating Password"
              alignSelf="flex-start"
            >
              Update Password
            </Button>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default AccountPage;